import React from 'react'
import { StyleSheet, TouchableOpacity, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Theme } from '@theme'
import { Icon, Text } from '../../../components/atoms'
import { useQRCode } from './hook'
import QRCodeSVG from 'react-native-qrcode-svg'
import { iconApp } from '@assets'
import { useScreen } from '@hooks'
import { getCommonStyles } from '@utils'
import { useLanguage } from '@context'

export const QRCodeContent: React.FC = () => {
  const theme = useTheme()
  const { isMobile } = useScreen()
  const { t } = useLanguage()
  const styles = getQRCodeContentStyles(theme)
  const commonStyles = getCommonStyles(theme)
  const { userName, profileUrl, onShareProfile, onCopyLink, onDownload } = useQRCode()

  return (
    <View style={styles.container}>
      {/* QR Code Section */}
      <View style={styles.qrSection}>
        <View style={styles.qrCodeContainer}>
          <QRCodeSVG
            value={profileUrl}
            size={240}
            color={theme.colors.primary}
            backgroundColor={theme.colors.white}
            logo={iconApp}
            logoSize={50}
            logoBackgroundColor={theme.colors.white}
            logoMargin={6}
            logoBorderRadius={12}
            quietZone={10}
          />
        </View>
        <Text type="bodyBold" color={theme.colors.primary} style={styles.userName}>
          {userName}
        </Text>
        <Text type="bodySmall" color={theme.colors.greyBlue} style={styles.profileUrl}>
          {profileUrl}
        </Text>
      </View>

      {/* Action Buttons */}
      <View style={[styles.buttonsContainer, !isMobile && styles.buttonsContainerNonMobile]}>
        <TouchableOpacity style={[styles.actionButton, isMobile && commonStyles.flex]} onPress={onShareProfile}>
          <Icon
            name="upload"
            size={32}
            color={theme.colors.primary}
            containerStyle={styles.buttonIcon}
          />
          <Text type="bodySmall" color={theme.colors.primary} style={styles.buttonText}>
            {t('qrCodeScreen.shareProfile')}
          </Text>
        </TouchableOpacity>

        <TouchableOpacity style={[styles.actionButton, isMobile && commonStyles.flex]} onPress={onCopyLink}>
          <Icon
            name="link2"
            size={32}
            color={theme.colors.primary}
            containerStyle={styles.buttonIcon}
          />
          <Text type="bodySmall" color={theme.colors.primary} style={styles.buttonText}>
            {t('qrCodeScreen.copyLink')}
          </Text>
        </TouchableOpacity>

        <TouchableOpacity style={[styles.actionButton, isMobile && commonStyles.flex]} onPress={onDownload}>
          <Icon
            name="download"
            size={32}
            color={theme.colors.primary}
            containerStyle={styles.buttonIcon}
          />
          <Text type="bodySmall" color={theme.colors.primary} style={styles.buttonText}>
            {t('qrCodeScreen.download')}
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  )
}

const getQRCodeContentStyles = (theme: Theme) =>
  StyleSheet.create({
    container: {
      flex: 1,
      alignItems: 'center',
      paddingVertical: theme.spacing.xl,
      paddingHorizontal: theme.spacing.lg,
    },
    qrSection: {
      alignItems: 'center',
      marginBottom: theme.spacing.xxxl,
      marginTop: theme.spacing.xl,
    },
    qrCodeContainer: {
      width: 280,
      height: 280,
      backgroundColor: theme.colors.white,
      borderRadius: theme.border.large,
      justifyContent: 'center',
      alignItems: 'center',
      marginBottom: theme.spacing.lg,
      shadowColor: theme.colors.black,
      shadowOffset: {
        width: 0,
        height: 2,
      },
      shadowOpacity: 0.1,
      shadowRadius: 4,
      elevation: 3,
      padding: theme.spacing.md,
    },
    userName: {
      fontSize: 18,
      textAlign: 'center',
      marginBottom: theme.spacing.xs,
    },
    profileUrl: {
      fontSize: 12,
      textAlign: 'center',
      opacity: 0.7,
    },
    buttonsContainer: {
      width: '100%',
      flexDirection: 'row',
      justifyContent: 'space-between',
      gap: theme.spacing.md,
    },
    buttonsContainerNonMobile: {
      justifyContent: 'center',
      gap: theme.spacing.xxxl,
    },
    actionButton: {
      alignItems: 'center',
      justifyContent: 'center',
    },
    buttonIcon: {
      marginBottom: theme.spacing.md,
    },
    buttonText: {
      textAlign: 'center',
      lineHeight: 16,
    },
  })
